import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2, Star, Send, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ReviewsPage() {
  const { biz, setBiz } = useOutletContext();
  const [sum, setSum] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [req, setReq] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/reviews/summary").then((r) => setSum(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (biz && !cfg) { setCfg({ google_review_url: biz.google_review_url || "", tripadvisor_review_url: biz.tripadvisor_review_url || "", review_threshold: biz.review_threshold || 4 }); } }, [biz, cfg]);

  const save = async () => {
    try { const { data } = await api.put("/business", { ...cfg, review_threshold: +cfg.review_threshold }); setBiz(data); toast.success("Review settings saved"); } catch { toast.error("Could not save"); }
  };
  const send = async () => {
    setSending(true);
    try { await api.post("/reviews/request", { ...req, origin_url: window.location.origin }); toast.success(`Review request sent to ${req.email}`); setReq({ name: "", email: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  if (!sum || !cfg) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;
  const stats = [
    { label: "Average rating", value: sum.average ?? "—" }, { label: "Ratings", value: sum.count },
    { label: `Sent to review sites (${sum.threshold}★+)`, value: sum.happy }, { label: "Kept private", value: sum.private },
    { label: "Google clicks", value: sum.clicks.google }, { label: "TripAdvisor clicks", value: sum.clicks.tripadvisor },
  ];
  return (
    <div className="space-y-8" data-testid="reviews-page">
      <div>
        <h1 className="text-3xl text-foreground mb-1">Review Booster</h1>
        <p className="text-muted-foreground text-sm">Happy guests are invited to review you publicly; lower ratings come to you privately.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-md p-4" data-testid={`review-stat-${s.label.split(" ")[0].toLowerCase()}`}>
            <p className="font-serif text-2xl text-foreground">{s.value}</p><p className="text-xs text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-5 space-y-3" data-testid="review-settings">
          <h3 className="font-serif text-lg text-foreground">Review links</h3>
          <div><Label className="text-xs">Google review link</Label><Input value={cfg.google_review_url} onChange={(e) => setCfg({ ...cfg, google_review_url: e.target.value })} placeholder="https://g.page/r/…/review" className="mt-1" data-testid="review-google-url" /></div>
          <div><Label className="text-xs">TripAdvisor review link</Label><Input value={cfg.tripadvisor_review_url} onChange={(e) => setCfg({ ...cfg, tripadvisor_review_url: e.target.value })} placeholder="https://www.tripadvisor.co.uk/UserReview-…" className="mt-1" data-testid="review-tripadvisor-url" /></div>
          <div><Label className="text-xs">Invite to review from (stars)</Label><Input type="number" min={1} max={5} value={cfg.review_threshold} onChange={(e) => setCfg({ ...cfg, review_threshold: e.target.value })} className="mt-1 w-24" data-testid="review-threshold" /></div>
          <Button variant="outline" onClick={save} data-testid="review-settings-save"><Save className="w-4 h-4 mr-1" /> Save</Button>
        </div>
        <div className="bg-card border border-border rounded-md p-5 space-y-3" data-testid="review-request">
          <h3 className="font-serif text-lg text-foreground">Ask a guest for a review</h3>
          <p className="text-xs text-muted-foreground">Emails a short note with a link to rate their {biz?.venue_type === "restaurant" ? "visit" : "stay"} in your hub.</p>
          <Input placeholder="Guest name" value={req.name} onChange={(e) => setReq({ ...req, name: e.target.value })} data-testid="review-req-name" />
          <Input placeholder="guest@email.com" value={req.email} onChange={(e) => setReq({ ...req, email: e.target.value })} data-testid="review-req-email" />
          <Button onClick={send} disabled={sending} data-testid="review-req-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send request</>}</Button>
        </div>
      </div>
      <div>
        <h3 className="font-serif text-lg text-foreground mb-3">Recent ratings</h3>
        {sum.feedback.length === 0 ? <p className="text-sm text-muted-foreground">No ratings yet.</p> : (
          <div className="bg-card border border-border rounded-md divide-y divide-border">
            {sum.feedback.map((f) => (
              <div key={f.id} className="p-4" data-testid={`review-feedback-${f.id}`}>
                <div className="flex items-center gap-2">
                  <span className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-3.5 h-3.5" fill={n <= f.rating ? "#C5A880" : "none"} color={n <= f.rating ? "#C5A880" : "#d4d4d8"} />)}</span>
                  <span className="text-sm text-foreground">{f.guest_name}{f.room ? ` · Room ${f.room}` : ""}</span>
                  {f.rating < sum.threshold && <span className="text-[10px] uppercase tracking-widest text-red-600">Private</span>}
                </div>
                {f.comment && <p className="text-sm text-muted-foreground mt-1">{f.comment}</p>}
                <p className="text-xs text-muted-foreground mt-1">{new Date(f.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
